import { getLogger } from "@logtape/logtape";
import type { z } from "zod";
import type { Endpoint, FetchEndpointRequestDataSchema } from "../schemas";
import { parseOpenApiSpecToEndpoints } from "../utils/openapi";

const logger = getLogger(["services-api", "preview-service"]);

export type PreviewServiceError = {
  code: string;
  message: string;
  details?: unknown;
};

export type PreviewServiceResult<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: PreviewServiceError;
    };

export type FetchEndpointRequestData = z.infer<
  typeof FetchEndpointRequestDataSchema
>;

export type FetchEndpointResponse = {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  url: string;
  method: string;
  duration: number;
};

/**
 * Fetches and parses the OpenAPI specification from a deployed app
 * @param appName - The name of the deployed app
 * @returns Array of endpoint definitions or empty array if spec not available
 */
export async function listAppEndpoints(
  appName: string,
): Promise<PreviewServiceResult<Endpoint[]>> {
  try {
    // Fetch OpenAPI spec from the deployed app
    const openApiUrl = `https://${appName}.fp.dev/openapi.json`;
    const openApiResponse = await fetch(openApiUrl);

    if (!openApiResponse.ok) {
      if (openApiResponse.status === 404) {
        // 404 could mean either:
        // 1. App exists but no OpenAPI spec
        // 2. App doesn't exist (Cloudflare returns 404 for non-existent workers)
        // We need to check if the app itself exists by analyzing response characteristics
        try {
          const baseResponse = await fetch(`https://${appName}.fp.dev/`, {
            // Add timeout to prevent hanging requests
            signal: AbortSignal.timeout(10000),
          });

          const contentType = baseResponse.headers.get("content-type");
          const server = baseResponse.headers.get("server");

          // Read response body once and analyze it
          const responseText = await baseResponse.text();

          // Analyze response characteristics to determine if worker exists
          if (baseResponse.status === 404) {
            // Check for specific Cloudflare Worker indicators
            const isWorkerResponse =
              contentType?.includes("application/json") ||
              responseText.includes('"message"') ||
              responseText.includes('"error"') ||
              server?.toLowerCase().includes("cloudflare-workers");

            const isGenericCloudflareError =
              responseText.includes("Worker not found") ||
              responseText.includes("The requested URL was not found") ||
              contentType?.includes("text/html");

            if (isWorkerResponse && !isGenericCloudflareError) {
              // Worker exists and returned a structured 404 - app is deployed
              return {
                success: false,
                error: {
                  code: "OPENAPI_NOT_FOUND",
                  message:
                    "OpenAPI specification not found - app deployed but no OpenAPI support",
                  details: { status: 404, responseType: "worker_response" },
                },
              };
            }

            if (isGenericCloudflareError) {
              // Generic Cloudflare response - worker doesn't exist
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, responseType: "cloudflare_generic" },
                },
              };
            }

            // Ambiguous case - be conservative and assume not deployed
            return {
              success: false,
              error: {
                code: "APP_NOT_DEPLOYED",
                message: "App not deployed",
                details: { status: 404, responseType: "ambiguous" },
              },
            };
          }
          // Base URL returned non-404, app definitely exists
          return {
            success: false,
            error: {
              code: "OPENAPI_NOT_FOUND",
              message:
                "OpenAPI specification not found - app deployed but no OpenAPI support",
              details: { status: 404, baseUrlStatus: baseResponse.status },
            },
          };
        } catch (error) {
          // Handle different types of errors
          logger.error("Error checking app deployment status", {
            appName,
            error,
          });
          if (error instanceof Error) {
            if (
              error.name === "AbortError" ||
              error.message.includes("timeout")
            ) {
              // Timeout - likely means app doesn't exist or network issues
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, error: "timeout" },
                },
              };
            }

            if (error.message.includes("fetch")) {
              // Network error - app likely doesn't exist
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, error: "network_error" },
                },
              };
            }
          }

          // Unknown error - be conservative and assume not deployed
          return {
            success: false,
            error: {
              code: "APP_NOT_DEPLOYED",
              message: "App not deployed",
              details: { status: 404, error: "unknown" },
            },
          };
        }
      }
      // Other errors (5xx, etc.) - treat as deployment issue
      return {
        success: false,
        error: {
          code: "APP_ERROR",
          message: "App deployment error",
          details: { status: openApiResponse.status },
        },
      };
    }

    const openApiSpec = await openApiResponse.json();

    // Parse OpenAPI spec to extract endpoints
    const endpoints = parseOpenApiSpecToEndpoints(openApiSpec);

    return {
      success: true,
      data: endpoints,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: "OPENAPI_FETCH_FAILED",
        message: "Failed to fetch OpenAPI specification",
        details: error,
      },
    };
  }
}

/**
 * Makes an HTTP request to a deployed app
 * @param appName - The name of the deployed app
 * @param requestData - The request configuration
 * @returns Response data with status, headers, body, and timing
 */
export async function fetchAppEndpoint(
  appName: string,
  requestData: FetchEndpointRequestData,
): Promise<PreviewServiceResult<FetchEndpointResponse>> {
  const startTime = Date.now();

  try {
    // Replace path parameters with actual values from params
    let processedPath = requestData.path;
    const usedParamNames = new Set<string>();

    if (requestData.params) {
      for (const param of requestData.params) {
        if (param.name && param.value) {
          const paramPattern = `{${param.name}}`;
          if (processedPath.includes(paramPattern)) {
            processedPath = processedPath.replace(paramPattern, param.value);
            usedParamNames.add(param.name);
          }
        }
      }
    }

    // Construct the full URL using the canonical app name and processed path
    const url = new URL(`https://${appName}.fp.dev${processedPath}`);

    // Add query parameters (excluding those used in path)
    if (requestData.params) {
      for (const param of requestData.params) {
        if (param.name && param.value && !usedParamNames.has(param.name)) {
          // Use append() to support repeated query parameters
          url.searchParams.append(param.name, param.value);
        }
      }
    }

    // Prepare headers
    const headers: Record<string, string> = {};
    if (requestData.headers) {
      for (const header of requestData.headers) {
        if (header.name && header.value) {
          headers[header.name] = header.value;
        }
      }
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
      method: requestData.method,
      headers,
    };

    if (requestData.body && !["GET", "HEAD"].includes(requestData.method)) {
      fetchOptions.body = requestData.body;
    }

    // Make the request to the deployed app
    const response = await fetch(url.toString(), fetchOptions);
    const responseBody = await response.text();
    const endTime = Date.now();

    // Collect response headers
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    return {
      success: true,
      data: {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: responseBody,
        url: url.toString(),
        method: requestData.method,
        duration: endTime - startTime,
      },
    };
  } catch (error) {
    let errorCode = "FETCH_ENDPOINT_FAILED";
    let errorMessage = "Failed to make fetch endpoint request";

    if (error instanceof TypeError && error.message.includes("fetch")) {
      errorCode = "INVALID_URL_OR_NETWORK_ERROR";
      errorMessage = "Invalid URL or network error";
    }

    return {
      success: false,
      error: {
        code: errorCode,
        message: errorMessage,
        details: error,
      },
    };
  }
}
